// src/services/api/PDFService.js の修正

import ApiService from '../base/BaseApiService';

/**
 * PDF関連のAPIサービス
 */
class PDFService {
  /**
   * ドキュメントを表示するためのURL取得
   * @param {number} documentId ドキュメントID
   * @returns {string} ドキュメント表示用URL
   */
  static getDocumentViewUrl(documentId) {
    if (!documentId) {
      console.error('ドキュメントIDが指定されていません');
      throw new Error('ドキュメントIDが必要です');
    }
    return `/insurance-applications/documents/${documentId}/view`;
  }

  /**
   * 保険ドキュメントを表示するためのURL取得
   * @param {string} fileTitle ファイル名
   * @returns {string} ドキュメント表示用URL
   */
  static getInsuranceDocumentViewUrl(fileTitle) {
    if (!fileTitle) {
      console.error('ファイル名が指定されていません');
      throw new Error('ファイル名が必要です');
    }
    // URLエンコードを行う
    const encodedTitle = encodeURIComponent(fileTitle);
    return `/insurance-document/${encodedTitle}/view`;
  }

  /**
   * 保険ドキュメントをダウンロードするためのURL取得
   * @param {string} fileTitle ファイル名
   * @returns {string} ドキュメントダウンロード用URL
   */
  static getInsuranceDocumentDownloadUrl(fileTitle) {
    if (!fileTitle) {
      console.error('ファイル名が指定されていません');
      throw new Error('ファイル名が必要です');
    }
    // URLエンコードを行う
    const encodedTitle = encodeURIComponent(fileTitle);
    return `/insurance-document/${encodedTitle}/download`;
  }

  /**
   * PDFドキュメントをArrayBufferとして取得
   * @param {string} url PDFのURL
   * @param {Object} params クエリパラメタ
   * @returns {Promise<ArrayBuffer>} PDFのArrayBuffer
   */
  static async fetchPdfDocument(url, params = {}) {
    try {
      console.log(`PDFドキュメントを取得します: ${url}`);
      
      // 絶対URLでない場合は相対パスと判断（デバッグ用メッセージ）
      if (!url.startsWith('http')) {
        console.log(`相対パスとしてPDFを取得します: ${url}`);
      }
      
      // 直接ApiServiceのメソッドを使用
      try {
        const pdfData = await ApiService.getPdf(url, params);
        
        // デバッグのためPDFデータの詳細をログに記録
        this.debugPdfData(pdfData);
        
        return pdfData;
      } catch (error) {
        // エラーが発生した場合、詳細なデバッグ情報を提供
        console.error(`PDFドキュメントの取得に失敗しました: ${error.message}`);
        
        // デバッグのために再試行（別の方法）
        console.log('フォールバック: 直接fetchを使用して再試行します');
        return await this.fetchPdfDocumentDirect(url);
      }
    } catch (error) {
      console.error('PDFドキュメントの取得に失敗しました:', error);
      throw new Error(`PDFドキュメントの取得に失敗しました: ${error.message}`);
    }
  }
  
  /**
   * フォールバックとして直接fetchを使ってPDFを取得する
   * @param {string} url PDFのURL
   * @returns {Promise<ArrayBuffer>} PDFのArrayBuffer
   */
  static async fetchPdfDocumentDirect(url) {
    try {
      // 完全なURLを構築（相対パスの場合）
      const apiUrl = url.startsWith('http') 
        ? url 
        : `${window.location.origin}${url}`;
      
      console.log(`直接fetchでPDFを取得します: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf, application/octet-stream',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
      }
      
      // レスポンスのContent-Typeを確認
      const contentType = response.headers.get('content-type');
      console.log(`レスポンスのContent-Type: ${contentType}`);
      if (contentType?.includes('text/html')) {
        console.warn(
          `fetchPdfDocumentDirect: HTMLレスポンス（${contentType}）を検出。ダウンロードを中止します。`
        );
        return null;
      }

      // レスポンスをArrayBufferとして取得
      const data = await response.arrayBuffer();
      
      if (!data || data.byteLength === 0) {
        throw new Error('PDFデータが空です');
      }
      
      console.log(`直接fetchでPDFデータを取得しました (${data.byteLength} バイト)`);
      return data;
    } catch (error) {
      console.error('直接fetchでのPDF取得に失敗しました:', error);
      throw error;
    }
  }

  /**
   * ドキュメントIDからPDFを直接取得
   * @param {number} documentId ドキュメントID 
   * @returns {Promise<ArrayBuffer>} PDFのArrayBuffer
   */
  static async fetchDocumentById(documentId) {
    try {
      if (!documentId) {
        throw new Error('ドキュメントIDが指定されていません');
      }
      
      const url = this.getDocumentViewUrl(documentId);
      return await this.fetchPdfDocument(url);
    } catch (error) {
      console.error(`ドキュメントID ${documentId} の取得に失敗しました:`, error);
      throw error;
    }
  }

  /**
   * PDFの中身をデバッグするためのメソッド
   * @param {ArrayBuffer} pdfData PDFデータ
   */
  static debugPdfData(pdfData) {
    if (!pdfData) {
      console.error('デバッグするPDFデータがありません');
      return;
    }
    
    // データサイズ
    console.log(`PDFデータサイズ: ${pdfData.byteLength} バイト`);
    
    // 先頭バイトを確認（PDFシグネチャをチェック）
    if (pdfData.byteLength >= 5) {
      const headerBytes = new Uint8Array(pdfData, 0, Math.min(20, pdfData.byteLength));
      let headerHex = '';
      let headerText = '';
      
      for (let i = 0; i < headerBytes.length; i++) {
        const byte = headerBytes[i];
        headerHex += byte.toString(16).padStart(2, '0') + ' ';
        
        // 表示可能なASCII文字のみテキストとして表示
        headerText += (byte >= 32 && byte <= 126) 
          ? String.fromCharCode(byte) 
          : '.';
      }
      
      console.log('PDF先頭バイト (16進数):', headerHex);
      console.log('PDF先頭バイト (テキスト):', headerText);
      
      // PDFシグネチャチェック
      const isPdf = headerText.startsWith('%PDF-');
      console.log('PDFシグネチャ確認:', isPdf ? '有効なPDF' : '無効なPDF');
    } else {
      console.error('PDFデータが短すぎます - シグネチャを確認できません');
    }
  }
  
  /**
   * バイナリデータをBlobに変換してURLを作成（テスト用）
   * @param {ArrayBuffer} data ArrayBufferデータ
   * @param {string} filename ファイル名
   * @returns {string} Blob URL
   */
  static createBlobUrl(data, filename = 'document.pdf') {
    try {
      // ArrayBufferからBlobを作成
      const blob = new Blob([data], { type: 'application/pdf' });
      
      // BlobからURLを作成
      const url = URL.createObjectURL(blob);
      
      console.log(`Blob URLを作成しました: ${url}`);
      return url;
    } catch (error) {
      console.error('Blob URLの作成に失敗しました:', error);
      throw error;
    }
  }
  
  /**
   * PDFをテスト表示する（デバッグ用）
   * @param {number} documentId ドキュメントID
   */
  static async testViewPdf(documentId) {
    try {
      const pdfData = await this.fetchDocumentById(documentId);
      const blobUrl = this.createBlobUrl(pdfData);
      
      // 新しいウィンドウでPDFを開く
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('PDFのテスト表示に失敗しました:', error);
      alert(`PDFの表示に失敗しました: ${error.message}`);
    }
  }
}

export default PDFService;